/**
 * CO2 calculation engine with exact formulas from Transmoove Excel file
 * Implements 4 scenarios: Refuse, Move & Re-use, Re-use & Refurbish, New
 */

import type { FurnitureData } from "@/components/calculator/FurnitureInputForm";

export interface ScenarioResult {
  scenario: "refuse" | "move-reuse" | "reuse-refurbish" | "new";
  co2Impact: number; // kg CO2e
  cost: number; // euros
}

export interface CalculationResult {
  scenarios: ScenarioResult[];
  savings: {
    co2: number;
    cost: number;
  };
}

export const EMISSION_FACTORS = {
  furnitureUnit: 180,
  kmPerKgCo2: 4.5,
} as const;

/**
 * Main CO2 calculator class
 * Implements exact formulas extracted from Excel file
 */
export class CO2Calculator {
  /**
   * Refuse scenario: Keep existing furniture
   * CO2 Impact = 0, Cost = 0
   */
  calculateRefuse(): ScenarioResult {
    return {
      scenario: "refuse",
      co2Impact: 0,
      cost: 0,
    };
  }

  /**
   * Move & Re-use scenario: Move and reuse furniture
   * CO2 Formula (Excel C42): ((fte * 6) / 30) * 105
   * Cost Formula (Excel D42): fte * 350 + 0.3 * officeArea
   */
  calculateMoveReuse(fte: number, officeArea: number): ScenarioResult {
    const co2Impact = ((fte * 6) / 30) * 105;
    const cost = fte * 350 + 0.3 * officeArea;

    return {
      scenario: "move-reuse",
      co2Impact: Math.round(co2Impact),
      cost: Math.round(cost),
    };
  }

  /**
   * Re-use & Refurbish scenario: Reuse with refurbishment
   * CO2 Formula: (totalFurniture * 180) / 2
   * Cost Formula (Excel D43): fte * 600 + 0.8 * officeArea
   */
  calculateReuseRefurbish(
    fte: number,
    officeArea: number,
    totalFurniture: number,
  ): ScenarioResult {
    const co2Impact = (totalFurniture * EMISSION_FACTORS.furnitureUnit) / 2;
    const cost = fte * 600 + 0.8 * officeArea;

    return {
      scenario: "reuse-refurbish",
      co2Impact: Math.round(co2Impact),
      cost: Math.round(cost),
    };
  }

  /**
   * New scenario: Purchase entirely new furniture
   * CO2 Formula: totalFurniture * 180
   * Cost Formula (Excel D44): (fte * 200) + (fte * 900) + 1.5 * officeArea
   */
  calculateNew(
    fte: number,
    officeArea: number,
    totalFurniture: number,
  ): ScenarioResult {
    const co2Impact = totalFurniture * EMISSION_FACTORS.furnitureUnit;
    const cost = fte * 200 + fte * 900 + 1.5 * officeArea;

    return {
      scenario: "new",
      co2Impact: Math.round(co2Impact),
      cost: Math.round(cost),
    };
  }

  /**
   * Calculates all scenarios and savings
   * Main method used by the interface
   */
  calculate(furnitureData: FurnitureData): CalculationResult {
    // Calculate total furniture count
    const totalFurniture =
      furnitureData.cabinets +
      furnitureData.individualDesks +
      furnitureData.officeChairs +
      furnitureData.canteenTables +
      furnitureData.meetingTables +
      furnitureData.visitorChairs;

    // Calculate all scenarios
    const refuse = this.calculateRefuse();
    const moveReuse = this.calculateMoveReuse(
      furnitureData.fte,
      furnitureData.officeArea,
    );
    const reuseRefurbish = this.calculateReuseRefurbish(
      furnitureData.fte,
      furnitureData.officeArea,
      totalFurniture,
    );
    const newScenario = this.calculateNew(
      furnitureData.fte,
      furnitureData.officeArea,
      totalFurniture,
    );

    const scenarios = [refuse, moveReuse, reuseRefurbish, newScenario];

    // Calculate savings compared to "New" scenario
    const savings = {
      co2: Math.max(0, newScenario.co2Impact - moveReuse.co2Impact),
      cost: Math.max(0, newScenario.cost - moveReuse.cost),
    };

    return {
      scenarios,
      savings,
    };
  }

  /**
   * Validates input data
   */
  validateInput(furnitureData: FurnitureData): {
    isValid: boolean;
    errors: string[];
  } {
    const errors: string[] = [];

    // Check numeric values
    Object.entries(furnitureData).forEach(([key, value]) => {
      if (typeof value !== "number" || Number.isNaN(value)) {
        errors.push(`${key}: non-numeric value`);
      } else if (value < 0) {
        errors.push(`${key}: negative value not allowed`);
      } else if (value > 10000) {
        errors.push(`${key}: value too high (max: 10000)`);
      }
    });

    return {
      isValid: errors.length === 0,
      errors,
    };
  }
}

// Singleton calculator instance
export const calculator = new CO2Calculator();

/**
 * Utility function to format numbers according to locale
 */
export const formatNumber = (
  num: number,
  locale: "fr-FR" | "nl-NL" = "fr-FR",
): string => {
  return new Intl.NumberFormat(locale).format(Math.round(num));
};

/**
 * Utility function to format currencies
 */
export const formatCurrency = (
  amount: number,
  locale: "fr-FR" | "nl-NL" = "fr-FR",
): string => {
  return new Intl.NumberFormat(locale, {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.round(amount));
};
